import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.tsx';
import { Flame, Mail, Lock, User, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { animate, stagger } from 'animejs';

export default function RegisterPage() {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState<'USER' | 'MANAGER'>('USER');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();
    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Boot-up animation sequence
        if (formRef.current) {
            animate('.register-element', {
                translateY: [30, 0],
                opacity: [0, 1],
                duration: 800,
                ease: 'outExpo',
                delay: stagger(80, { start: 200 }),
            });
        }
    }, []);

    // Password strength indicator
    const getPasswordStrength = (pwd: string) => {
        if (pwd.length === 0) return { strength: 0, label: '', color: 'bg-gray-600' };
        if (pwd.length < 8) return { strength: 1, label: 'Weak', color: 'bg-red-500' };

        let strength = 1;
        if (/[A-Z]/.test(pwd)) strength++;
        if (/[a-z]/.test(pwd)) strength++;
        if (/[0-9]/.test(pwd)) strength++;
        if (/[^A-Za-z0-9]/.test(pwd)) strength++;

        if (strength <= 2) return { strength: 2, label: 'Fair', color: 'bg-yellow-500' };
        if (strength === 3) return { strength: 3, label: 'Good', color: 'bg-green-400' };
        return { strength: 4, label: 'Strong', color: 'bg-green-500' };
    };

    const passwordStrength = getPasswordStrength(password);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            await register(email, password, fullName, role);
            navigate('/dashboard');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Registration failed');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden blueprint-grid"
            style={{ background: 'var(--dark-bg)' }}
        >
            {/* Ambient Fire Glow */}
            <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                <div
                    className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-float"
                    style={{ background: 'var(--fire-red)' }}
                />
                <div
                    className="absolute bottom-1/4 left-1/4 w-72 h-72 rounded-full blur-3xl opacity-15 animate-float"
                    style={{ background: 'var(--fire-orange)', animationDelay: '1s' }}
                />
            </div>

            {/* Radar Pulse */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
                <div
                    className="w-64 h-64 rounded-full border-2 opacity-20"
                    style={{
                        borderColor: 'var(--fire-orange)',
                        animation: 'radarPulse 3s cubic-bezier(0.16, 1, 0.3, 1) infinite'
                    }}
                />
            </div>

            {/* Technical Status Labels */}
            <div className="absolute top-8 right-8 tech-label opacity-70 hidden md:block" aria-hidden="true">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                REGISTRATION PORTAL
            </div>
            <div className="absolute bottom-8 left-8 tech-label opacity-70 hidden md:block" aria-hidden="true">
                ENROLL_SYS v2.4.1
            </div>

            <div ref={formRef} className="w-full max-w-md relative z-10 py-8">
                {/* Logo & Brand */}
                <div className="text-center mb-8 register-element opacity-0">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 animate-float"
                        style={{
                            background: 'linear-gradient(135deg, var(--fire-red), var(--fire-orange))',
                            boxShadow: '0 0 40px rgba(255, 107, 53, 0.4)'
                        }}
                    >
                        <Flame className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Create Account
                    </h1>
                    <p className="text-gray-400">
                        Join the compliance platform
                    </p>
                </div>

                {/* Register Card */}
                <div className="card-terminal p-8 register-element opacity-0">
                    {/* Terminal Header */}
                    <div className="flex items-center gap-2 mb-6 pb-4 border-b border-[var(--blueprint-line)]">
                        <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-500/80" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                            <div className="w-3 h-3 rounded-full bg-green-500/80" />
                        </div>
                        <span className="text-xs text-gray-500 font-mono ml-2">REGISTRATION_TERMINAL</span>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Error Alert */}
                        {error && (
                            <div className="alert-terminal">
                                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                <p className="alert-terminal-text">{error}</p>
                            </div>
                        )}

                        {/* Full Name */}
                        <div className="register-element opacity-0">
                            <label htmlFor="fullName" className="label-terminal">
                                Full Name
                            </label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <input
                                    id="fullName"
                                    type="text"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="input-terminal pl-12"
                                    placeholder="John Doe"
                                    required
                                    autoComplete="name"
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div className="register-element opacity-0">
                            <label htmlFor="email" className="label-terminal">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-terminal pl-12"
                                    placeholder="you@company.com"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        {/* Role Selection */}
                        <div className="register-element opacity-0">
                            <label className="label-terminal">Account Type</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setRole('USER')}
                                    className={`p-4 rounded-lg border transition-all ${role === 'USER'
                                        ? 'border-[var(--fire-orange)] bg-[var(--fire-orange)]/10'
                                        : 'border-[var(--blueprint-line)] bg-transparent hover:border-gray-500'
                                        }`}
                                >
                                    <div className="text-sm font-medium text-white">User</div>
                                    <div className="text-xs text-gray-500 mt-1">View & Report</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRole('MANAGER')}
                                    className={`p-4 rounded-lg border transition-all ${role === 'MANAGER'
                                        ? 'border-[var(--fire-orange)] bg-[var(--fire-orange)]/10'
                                        : 'border-[var(--blueprint-line)] bg-transparent hover:border-gray-500'
                                        }`}
                                >
                                    <div className="text-sm font-medium text-white">Manager</div>
                                    <div className="text-xs text-gray-500 mt-1">Full Access</div>
                                </button>
                            </div>
                        </div>

                        {/* Password */}
                        <div className="register-element opacity-0">
                            <label htmlFor="password" className="label-terminal">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input-terminal pl-12"
                                    placeholder="••••••••"
                                    required
                                    autoComplete="new-password"
                                />
                            </div>
                            {/* Password Strength */}
                            {password && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4].map((level) => (
                                            <div
                                                key={level}
                                                className={`h-1 flex-1 rounded-full transition-all ${level <= passwordStrength.strength
                                                    ? passwordStrength.color
                                                    : 'bg-gray-700'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Strength: <span className="font-medium text-gray-400">{passwordStrength.label}</span>
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div className="register-element opacity-0">
                            <label htmlFor="confirmPassword" className="label-terminal">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="input-terminal pl-12"
                                    placeholder="••••••••"
                                    required
                                    autoComplete="new-password"
                                />
                                {confirmPassword && password === confirmPassword && (
                                    <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                                )}
                            </div>
                        </div>

                        {/* Terms */}
                        <label className="flex items-start gap-3 cursor-pointer group register-element opacity-0">
                            <input
                                type="checkbox"
                                required
                                className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-transparent text-[var(--fire-orange)] focus:ring-2 focus:ring-[var(--fire-orange)]/50 focus:ring-offset-0"
                            />
                            <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                                I agree to the{' '}
                                <Link to="/terms" className="text-[var(--fire-orange)] hover:text-white font-medium">
                                    Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link to="/privacy" className="text-[var(--fire-orange)] hover:text-white font-medium">
                                    Privacy Policy
                                </Link>
                            </span>
                        </label>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-terminal-primary w-full flex items-center justify-center gap-2 register-element opacity-0"
                        >
                            {loading ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Creating account...</span>
                                </>
                            ) : (
                                <>
                                    <span>Create Account</span>
                                    <ArrowRight className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Login Link */}
                    <div className="mt-6 text-center text-sm text-gray-500 register-element opacity-0">
                        Already have an account?{' '}
                        <Link
                            to="/login"
                            className="text-[var(--fire-orange)] hover:text-white font-medium transition-colors"
                        >
                            Sign in
                        </Link>
                    </div>
                </div>

                {/* Footer */}
                <p className="text-center text-sm text-gray-600 mt-8 register-element opacity-0">
                    © 2026 Environaise. Built with care for engineers.
                </p>
            </div>
        </div>
    );
}
